import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { SubmitHandler, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { toast } from 'sonner'
import { z } from 'zod'

import { queryKeys } from '@/constants/queryKeys'
import { useErrorToast } from '@/hooks/useErrorToast'
import { queryClient } from '@/lib/queryClient'
import { getAnimationKey } from '@/lib/utils'
import {
  AnimatedFormLabel,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  Input,
  MotionDiv,
  Spinner,
} from '@/shared/ui'

import { getContractToSign, signContract } from '../../api'
import { ContractorDetails, ContractState } from '../../types'

const CONTRACT_SIGN_FORM_ID = 'contract-sign-form-id'

const signContractSchema = z.object({
  signedByFullName: z
    .string()
    .min(1, { message: 'validation.signature.required' }),
})

type SignContractSchema = z.infer<typeof signContractSchema>

type Props = {
  contractor?: ContractorDetails
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
}

export const SignContractDialog = ({
  contractor,
  isOpen,
  onOpenChange,
}: Props) => {
  const intl = useIntl()
  const notifyError = useErrorToast()

  const { mutateAsync, isPending, isSuccess } = useMutation({
    mutationFn: signContract,
  })

  const form = useForm<SignContractSchema>({
    mode: 'onChange',
    resolver: zodResolver(signContractSchema),
    values: {
      signedByFullName: '',
    },
  })

  const latestContract = contractor?.contracts[0]

  const contractToSignQuery = useQuery({
    queryKey: [queryKeys.getContractToSign],
    queryFn: () =>
      getContractToSign({
        contractId: latestContract?.id ?? '',
      }),
    select: (data) => data.data,
    enabled: !!latestContract?.id,
  })

  const onSubmit: SubmitHandler<SignContractSchema> = async (data) => {
    try {
      await mutateAsync({
        contractId: latestContract?.id ?? '',
        documentId: contractToSignQuery.data?.documentId ?? '',
        ...data,
      })

      toast.success(
        intl.formatMessage({
          id: 'contractor.onboarding.signContract.success',
          defaultMessage: 'Contract signed successfully',
        }),
      )

      queryClient.invalidateQueries({
        queryKey: [queryKeys.getContractor, contractor?.id],
      })

      queryClient.invalidateQueries({
        queryKey: [queryKeys.getAllContractDocuments, latestContract?.id],
      })

      onOpenChange(false)
    } catch (error) {
      notifyError(error)
    }
  }

  const hasAmendedContract = !!contractor?.contracts.find(
    (contract) => contract.state === ContractState.AMENDED,
  )

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-full lg:max-w-3xl"
        hideCloseButtonOnDesktop={false}
      >
        <DialogTitle>
          <FormattedMessage
            id="contractor.dialog.signTheContract.title"
            defaultMessage="Sign the contract with {name}"
            values={{
              name: contractor?.personalDetails.firstName,
            }}
          />
        </DialogTitle>

        <div className="p-1" />

        <DialogDescription>
          {hasAmendedContract ? (
            <FormattedMessage
              id="contractor.dialog.signTheAmendedContract.description"
              defaultMessage="{name} has already signed their contract, we are just missing your signature to make it effective"
              values={{
                name: contractor?.personalDetails.firstName,
              }}
            />
          ) : (
            <FormattedMessage
              id="contractor.dialog.signTheContract.description"
              defaultMessage="{name} has already signed their contract, we are just missing your signature to finalize the onboarding"
              values={{
                name: contractor?.personalDetails.firstName,
              }}
            />
          )}
        </DialogDescription>

        <div className="p-4" />

        <MotionDiv
          key={getAnimationKey(contractToSignQuery.isPending)}
          className="border-neutral-gray-200 flex max-h-[200px] flex-col gap-12 overflow-auto rounded-xl border p-4 md:max-h-[400px]"
        >
          {contractToSignQuery.data ? (
            contractToSignQuery.data?.base64pages.map((page, index) => (
              <img
                key={index}
                alt={intl.formatMessage({
                  id: 'label.standardContract',
                  defaultMessage: 'Standard contract',
                })}
                src={`data:image/png;base64,${page}`}
                width="100%"
                height="100%"
                className="rounded-xl"
              />
            ))
          ) : (
            <div className="flex items-center justify-center md:h-[400px]">
              <Spinner className="size-16" />
            </div>
          )}
        </MotionDiv>

        <div className="p-4" />

        <Form {...form}>
          <form
            className="mx-auto w-full max-w-md"
            id={CONTRACT_SIGN_FORM_ID}
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <FormField
              control={form.control}
              name="signedByFullName"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      variant="2fa"
                      className="font-(family-name:--font-pacifico)"
                      placeholder={intl.formatMessage({
                        defaultMessage: 'Your signature',
                        id: 'label.signature',
                      })}
                      {...field}
                    />
                  </FormControl>
                  <AnimatedFormLabel>
                    <FormattedMessage
                      defaultMessage="Your signature"
                      id="label.signature"
                    />
                  </AnimatedFormLabel>
                </FormItem>
              )}
            />
          </form>
        </Form>

        <div className="p-4" />

        <div className="mx-auto w-full max-w-md">
          <Button
            onClick={form.handleSubmit(onSubmit)}
            disabled={isPending || isSuccess}
            loading={isPending || isSuccess}
            width="full"
            id={CONTRACT_SIGN_FORM_ID}
            type="submit"
          >
            <FormattedMessage
              id="action.applyAndSign"
              defaultMessage="Apply & sign"
            />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
